(() => {
  "use strict";

  if (window.__ELOOKUS_LENS_FLOW_V2_RUNTIME_FIX__) return;
  window.__ELOOKUS_LENS_FLOW_V2_RUNTIME_FIX__ = true;

  const CART_PATH = /\/cart\.js(?:[?#]|$)/;
  const CART_ADD_PATH = /\/cart\/add(?:\.js)?(?:[?#]|$)/;
  const PAIR_PROPERTY = "_elookus_pair_id";
  const REVIEW_PANEL_SELECTOR = ".elv2-overlay .elv2-panel[data-step='review']:not([hidden])";
  const NEXT_BUTTON_SELECTOR = ".elv2-overlay [data-elv2-next]";
  const GLOBO_ROOT_SELECTOR = ".gpo-app,#gpo-app,[data-gpo-app],.gpo-container,[class*='gpo-app']";
  const nativeFetch = window.fetch.bind(window);
  const nativeAddEventListener = EventTarget.prototype.addEventListener;
  const nativeDispatchEvent = EventTarget.prototype.dispatchEvent;

  let pendingPairId = "";
  let pendingPairCartPromise = null;
  let pendingPairCartKey = "";
  let suppressResetEvents = false;
  let multiPairWrapTimer = 0;
  let uiObserver = null;
  let uiNormalizeTimer = 0;

  function injectContainmentStyles() {
    if (document.getElementById("elookus-lens-flow-v2-runtime-fix-styles")) return;
    const style = document.createElement("style");
    style.id = "elookus-lens-flow-v2-runtime-fix-styles";
    style.textContent = `
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) .gpo-element:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-option-id]:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-id^="radio_"]:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-id^="dropdown_"]:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-id^="file_"]:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-id^="number_"]:not([data-elv2-field]),
      html.elv2-lens-ready :is(${GLOBO_ROOT_SELECTOR}) > [data-id^="paragraph_"]:not([data-elv2-field]) {
        display: none !important;
      }
      [data-elookus-globo-native-notice="hidden"] {
        display: none !important;
      }
    `;
    (document.head || document.documentElement).append(style);
  }

  function absoluteUrl(input) {
    try {
      const raw = typeof input === "string" ? input : input?.url;
      return new URL(raw || "", location.href);
    } catch (_error) {
      return null;
    }
  }

  function itemProperties(item) {
    if (Array.isArray(item?.properties)) {
      return item.properties.map((entry) => [String(entry?.name || ""), entry?.value]);
    }
    return Object.entries(item?.properties || {});
  }

  function itemProperty(item, key) {
    const match = itemProperties(item).find(([name]) => name === key);
    return match ? String(match[1] ?? "").trim() : "";
  }

  function cartHasPair(cart, pairId) {
    return Boolean(
      pairId &&
      Array.isArray(cart?.items) &&
      cart.items.some((item) => itemProperty(item, PAIR_PROPERTY) === pairId)
    );
  }

  function pairIdFromBody(body) {
    if (!body) return "";
    try {
      if (typeof FormData !== "undefined" && body instanceof FormData) {
        return String(body.get(`properties[${PAIR_PROPERTY}]`) || "").trim();
      }
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        return String(body.get(`properties[${PAIR_PROPERTY}]`) || "").trim();
      }
      if (typeof body === "string") {
        const trimmed = body.trim();
        if (!trimmed) return "";
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
          const parsed = JSON.parse(trimmed);
          const properties = parsed?.properties || parsed?.items?.[0]?.properties || {};
          return String(properties?.[PAIR_PROPERTY] || "").trim();
        }
        const params = new URLSearchParams(trimmed);
        return String(params.get(`properties[${PAIR_PROPERTY}]`) || "").trim();
      }
    } catch (_error) {
      return "";
    }
    return "";
  }

  async function fetchLatestCart() {
    const response = await nativeFetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  function delay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  async function pollForPair(pairId) {
    const deadline = window.performance.now() + 9000;
    let latest = null;

    do {
      if (pendingPairId && pendingPairId !== pairId) break;
      try {
        latest = await fetchLatestCart();
        if (cartHasPair(latest, pairId)) return latest;
      } catch (_error) {
        // Keep polling within the bounded confirmation window.
      }
      if (!pendingPairId) break;
      await delay(160);
    } while (window.performance.now() < deadline);

    if (latest) return latest;
    throw new Error("Unable to read the latest cart");
  }

  function waitForPairCart(pairId) {
    if (!pairId) return fetchLatestCart();
    if (pendingPairCartPromise && pendingPairCartKey === pairId) return pendingPairCartPromise;

    pendingPairCartKey = pairId;
    pendingPairCartPromise = pollForPair(pairId).finally(() => {
      pendingPairCartPromise = null;
      pendingPairCartKey = "";
    });
    return pendingPairCartPromise;
  }

  function cartResponse(cart) {
    return new Response(JSON.stringify(cart), {
      status: 200,
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "Cache-Control": "no-store",
      },
    });
  }

  window.fetch = async function elookusFreshCartFetch(input, init) {
    const url = absoluteUrl(input);
    const isCartAdd = Boolean(url && CART_ADD_PATH.test(url.pathname + url.search));
    const isCartRead = Boolean(url && CART_PATH.test(url.pathname + url.search));
    const pairId = isCartAdd ? pairIdFromBody(init?.body) : "";

    if (pairId) pendingPairId = pairId;

    const response = await nativeFetch(input, init);

    if (isCartAdd && pairId && !response.ok && pendingPairId === pairId) {
      pendingPairId = "";
    }

    if (isCartRead && pendingPairId) {
      const pairCart = await waitForPairCart(pendingPairId);
      if (cartHasPair(pairCart, pendingPairId)) pendingPairId = "";
      return cartResponse(pairCart);
    }

    return response;
  };

  function isGloboControl(target) {
    return target instanceof Element && Boolean(target.closest(GLOBO_ROOT_SELECTOR));
  }

  EventTarget.prototype.dispatchEvent = function elookusDeferredResetDispatch(event) {
    if (
      suppressResetEvents &&
      event &&
      (event.type === "change" || event.type === "input") &&
      isGloboControl(this)
    ) {
      return true;
    }
    return nativeDispatchEvent.call(this, event);
  };

  EventTarget.prototype.addEventListener = function elookusRuntimeFixAdd(type, listener, options) {
    if (
      type === "click" &&
      this instanceof Element &&
      this.matches("[data-elv2-close]") &&
      typeof listener === "function"
    ) {
      const wrappedClose = function wrappedElookusClose(event) {
        const completed = Boolean(
          document.querySelector(".elv2-cart-feedback[data-state='success']")
        );
        if (!completed) return listener.call(this, event);

        suppressResetEvents = true;
        try {
          return listener.call(this, event);
        } finally {
          suppressResetEvents = false;
          window.requestAnimationFrame(normalizeLensFlowUi);
        }
      };
      return nativeAddEventListener.call(this, type, wrappedClose, options);
    }

    if (
      this === document &&
      ["cart:updated", "cart:refresh", "theme:cart:updated"].includes(type) &&
      typeof listener === "function" &&
      listener.name === "handleThemeCartUpdate"
    ) {
      const wrappedCartListener = function wrappedElookusCartListener(event) {
        const cart = event?.detail?.cart || event?.detail;
        if (pendingPairId && Array.isArray(cart?.items) && !cartHasPair(cart, pendingPairId)) {
          return undefined;
        }
        return listener.call(this, event);
      };
      return nativeAddEventListener.call(this, type, wrappedCartListener, options);
    }

    return nativeAddEventListener.call(this, type, listener, options);
  };

  function dispatchFreshCart(cart) {
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
        cancelable: false,
        detail: { cart },
      })
    );
  }

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest?.(NEXT_BUTTON_SELECTOR);
      if (!button || button.dataset.elookusFreshCartBypass === "true") return;
      if (!document.querySelector(REVIEW_PANEL_SELECTOR) || button.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const wasDisabled = button.disabled;
      button.disabled = true;

      fetchLatestCart()
        .then(dispatchFreshCart)
        .catch((error) => {
          console.warn(`[ELOOKUS Cart Runtime Fix] Fresh cart preflight failed: ${error.message}`);
        })
        .finally(() => {
          if (!button.isConnected) return;
          button.disabled = wasDisabled;
          button.dataset.elookusFreshCartBypass = "true";
          button.click();
          window.queueMicrotask(() => delete button.dataset.elookusFreshCartBypass);
        });
    },
    true
  );

  function wrapMultiPairApi() {
    const api = window.ElookusMultiPairCartSync;
    if (!api || api.__elookusFreshCartV1) return Boolean(api?.__elookusFreshCartV1);

    const originalSync = api.sync.bind(api);
    const originalSchedule = api.schedule.bind(api);
    const wrapped = {
      __elookusFreshCartV1: true,
      async sync() {
        const pairId = pendingPairId;
        let latest = pairId ? await waitForPairCart(pairId) : await fetchLatestCart();
        if (pairId && cartHasPair(latest, pairId) && pendingPairId === pairId) pendingPairId = "";

        const result = await originalSync(latest);
        latest = await fetchLatestCart();
        return {
          cart: latest,
          changed: Boolean(result?.changed),
          settled: result?.settled !== false,
        };
      },
      schedule(cart) {
        return originalSchedule(cart);
      },
    };

    window.ElookusMultiPairCartSync = Object.freeze(wrapped);
    return true;
  }

  function scheduleMultiPairWrap() {
    if (wrapMultiPairApi()) return;
    let attempts = 0;
    multiPairWrapTimer = window.setInterval(() => {
      attempts += 1;
      if (wrapMultiPairApi() || attempts >= 80) {
        window.clearInterval(multiPairWrapTimer);
        multiPairWrapTimer = 0;
      }
    }, 125);
  }

  function hideGloboNativePriceNotices() {
    const scopes = new Set();
    document.querySelectorAll(GLOBO_ROOT_SELECTOR).forEach((root) => {
      scopes.add(root.closest(".product-info,.product-form,[data-product-form],.product-detail") || root.parentElement || root);
    });

    scopes.forEach((scope) => {
      const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
      const matches = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const text = String(node.nodeValue || "").replace(/\s+/g, " ").trim();
        if (/^Selections? will add .* to the price$/i.test(text)) matches.push(node.parentElement);
      }
      matches.forEach((element) => {
        if (!element || element.closest(".elv2-overlay")) return;
        const container = element.closest(".gpo-total,.gpo-price,.gpo-price-container") || element;
        container.dataset.elookusGloboNativeNotice = "hidden";
      });
    });
  }

  function normalizeLensFlowUi() {
    document.querySelectorAll(".elv2-cart-checkout").forEach((link) => {
      link.setAttribute("href", "/cart");
      const label = link.querySelector(".elv2-label > span") || link;
      if (label.textContent.trim() !== "Review Cart & Offers") {
        label.textContent = "Review Cart & Offers";
      }
    });
    hideGloboNativePriceNotices();
  }

  function scheduleLensFlowUiNormalize() {
    window.clearTimeout(uiNormalizeTimer);
    uiNormalizeTimer = window.setTimeout(() => {
      uiNormalizeTimer = 0;
      normalizeLensFlowUi();
    }, 80);
  }

  function observeLensFlowUi() {
    normalizeLensFlowUi();
    if (!document.body || uiObserver) return;
    uiObserver = new MutationObserver(scheduleLensFlowUiNormalize);
    uiObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  injectContainmentStyles();
  scheduleMultiPairWrap();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeLensFlowUi, { once: true });
  } else {
    observeLensFlowUi();
  }
})();
